/**
 * resolveInstruction — build the chat-composer instruction that
 * "Resolve with asthra" pre-fills for a section issue.
 *
 * The instruction is seeded (not auto-sent) into the composer with the
 * marker's paragraph already highlighted + captured into the selection pill,
 * so it rides the normal edit-highlight pipeline: composer → handleSendChat →
 * intent router → useHighlightEditFlow.start → /api/selection-edit. The writer
 * reviews and presses send, keeping the human in the loop.
 *
 * Two shapes, keyed on ``issue.kind``:
 *   data_gap   — resolve every missing-info flag in the highlighted span from
 *                the library; leave unresolved flags in place; report if none
 *                could be resolved.
 *   regulatory — resolve THIS specific flag using the finding's own context
 *                (finding_issue) + recommended fix (finding_resolution); leave
 *                every other flag in the span untouched; report if the needed
 *                context can't be found.
 */

import type { SectionIssue } from "../../api/client";

export const MISSING_INFO_INSTRUCTION =
  "Identify every missing-info flag in the highlighted text. Search the library and fill in as many as you can, " +
  "rewriting the highlighted text with the information you find. Keep any flag you can't resolve exactly where it is " +
  "in the rewritten text — don't remove it. If you can't resolve any of them, leave the text unchanged and tell me " +
  "they couldn't be resolved.";

/** The regulatory flag's identity for the instruction: prefer its own context,
 *  fall back to the verbatim marker so the sentence stays coherent when the
 *  audit finding didn't join (finding_issue null on older runs). */
function flagIdentity(issue: SectionIssue): string {
  const issueText = issue.finding_issue?.trim();
  if (issueText) return issueText;
  const marker = issue.marker_text?.trim();
  return marker || "this regulatory flag";
}

function buildRegulatoryInstruction(issue: SectionIssue): string {
  const lines: string[] = ["Resolve this specific regulatory flag by rewriting the highlighted text.", ""];

  const issueText = issue.finding_issue?.trim();
  const fixText = issue.finding_resolution?.trim();
  if (issueText) lines.push(`Issue: ${issueText}`);
  else lines.push(`Issue: ${flagIdentity(issue)}`);
  if (fixText) lines.push(`Recommended fix: ${fixText}`);
  lines.push("");

  lines.push(
    "Rewrite the highlighted text to resolve only this issue. Leave any other missing-info flags or regulatory " +
      "flags in the highlighted text exactly as they are. If you can't find the information needed to resolve it, " +
      "leave the text unchanged and tell me it couldn't be resolved.",
  );
  return lines.join("\n");
}

/** Compose the pre-fill instruction for a "Resolve with asthra" click. */
export function buildResolveInstruction(issue: SectionIssue): string {
  if (issue.kind === "regulatory") return buildRegulatoryInstruction(issue);
  return MISSING_INFO_INSTRUCTION;
}
